import React, { useEffect, useRef, FunctionComponent, ChangeEventHandler, ChangeEvent, Ref } from 'react';
import styled from 'styled-components';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useForm, Controller } from 'react-hook-form';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { NumericFormat, NumberFormatValues, SourceInfo, NumberFormatBaseProps } from 'react-number-format';
import { FieldWrapper } from '../Form';
import { Dropdown } from 'primereact/dropdown';
import { TargetReasonEnum } from '../../common/enum';

// Validation schema using Yup
const validationSchema = yup
  .object({
    value: yup.string().nullable().required('This is a required field'),
    oldValue: yup.string().nullable().required('This is a required field'),
    reasonId: yup.number().nullable().min(1, 'This is a required field').required('This is a required field')
  })
  .required();

// Styled component for custom input addon
const CustomInputAddon = styled.span`
  font-size: 0.875rem;
  padding: 0.65625rem 0.65625rem;
`;

// Props for the DialogFooter component
interface DialogFooterProps {
  onCancel?: () => void;
  onSubmit?: () => void;
}

// Footer component for the dialog
const DialogFooter: React.FC<DialogFooterProps> = ({ onCancel = () => {}, onSubmit = () => {} }) => {
  return (
    <div>
      <Button label="Cancel" onClick={onCancel} size="small" text />
      <Button label="Submit" onClick={onSubmit} size="small" />
    </div>
  );
};

// Props for the NumericInput component
interface NumericInputProps extends NumberFormatBaseProps {
  onChange: ChangeEventHandler<HTMLInputElement>;
}

// Definition of the NumericInput component
const NumericInputDef: FunctionComponent<NumericInputProps> = ({ onChange, ...props }) => (
  <NumericFormat
    {...props}
    type="text"
    thousandSeparator=" "
    decimalSeparator=","
    allowNegative={false}
    allowedDecimalSeparators={[',', '.']}
    valueIsNumericString={true} // Treats value as a numeric string
    // Handle value change
    onValueChange={(values: NumberFormatValues, sourceInfo: SourceInfo) => {
      onChange(
        Object.assign({} as ChangeEvent<HTMLInputElement>, sourceInfo.event, {
          target: { name: props.name, value: values.value },
        }),
      );
    }}
  />
);

// Forwarding ref to NumericInput component
const NumericInput = React.forwardRef<HTMLInputElement, NumericInputProps>((props, ref) => (
  <NumericInputDef {...props} customInput={InputText as any} getInputRef={ref as Ref<HTMLInputElement>} />
));

// Props for the EditTargetCellModal component
interface EditTargetCellModalProps {
  item: any;
  defaultReason?: number;
  reasonsData: any[];
  onSubmit: (data: any) => void;
  onClose: () => void;
}

// Main component for editing target cell modal
const EditTargetCellModal: React.FC<EditTargetCellModalProps> = ({
  item,
  defaultReason,
  reasonsData,
  onSubmit,
  onClose,
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isDivisional = item?.data.isDivisionalTarget;
  const isInitialEntry = item.data.reasonId === 0 && item.data.value === 0 && item.data.oldValue === 0;
  const initialReasonId = isInitialEntry ? TargetReasonEnum.InitialEntry : 0;
  // Form hook with validation schema
  const { control, handleSubmit } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      value: item.data.value,
      oldValue: item.data.oldValue,
      reasonId: initialReasonId,
    },
  });

  // Submit handler for the form
  const submit = () => formRef.current?.requestSubmit();

  useEffect(() => {
    if (item) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 100);
    }
  }, [item]);

  // Handle focus event to select the input value
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      event.target.select();
    }, 0);
  };

  return (
    <Dialog
      style={{ width: '400px' }}
      header="Edit target value"
      visible={!!item}
      onHide={onClose}
      footer={<DialogFooter onCancel={onClose} onSubmit={submit} />}
      modal
    >
      <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
        <FieldWrapper>
          <label htmlFor="name">Updated value</label>
          <Controller
            name="value"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <div className="p-inputgroup flex-1">
                  <NumericInput
                    {...field}
                    className={classNames('p-inputtext-sm', { 'p-invalid': fieldState.error })}
                    onFocus={handleFocus}
                    ref={inputRef}
                    autoComplete="off" // Disable browser autofill suggestions
                  />
                  <CustomInputAddon className="p-inputgroup-addon" style={{ fontSize: '14px' }}>
                    {item.data.metricUnit}
                  </CustomInputAddon>
                </div>
                {fieldState.error && <small className="p-error">{fieldState.error.message}</small>}
              </>
            )}
          />
          <Controller
            name="oldValue"
            control={control}
            render={({ field }) => (
              <>
                <InputText type="hidden" id={field.name} value={field.value} ref={field.ref} />
              </>
            )}
          />
        </FieldWrapper>
        {isDivisional && (
          <FieldWrapper>
            <label htmlFor="reasonId">Reason for editing value</label>
            <Controller
              name="reasonId"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <Dropdown
                    id={field.name}
                    options={reasonsData}
                    value={field.value}
                    optionLabel="description"
                    optionValue="id"
                    className={classNames({ 'p-invalid': fieldState.error })}
                    onChange={(e) => field.onChange(e.value)}
                    placeholder="Select reason"
                  />
                  {fieldState.error && <small className="p-error">{fieldState.error.message}</small>}
                </>
              )}
            />
          </FieldWrapper>
        )}
      </form>
    </Dialog>
  );
};

export default EditTargetCellModal;
